// PCR master-mix scaling. A master mix contains every reagent shared across
// reactions (everything except the per-tube template), scaled by the reaction
// count plus an overage to cover pipetting loss.

export interface PcrComponent {
  /** reagent name */
  name: string;
  /** volume per single reaction, in µL */
  perRxn: number;
}

export interface PcrRow extends PcrComponent {
  /** scaled volume for all reactions incl. overage, in µL */
  total: number;
}

export interface PcrResult {
  rows: PcrRow[];
  /** effective number of reactions (count × (1 + overage)) */
  effectiveRxns: number;
  /** master-mix volume per tube (sum of per-rxn), in µL */
  perTube: number;
  /** total master-mix volume across all reactions, in µL */
  totalVolume: number;
}

/**
 * Scale a per-reaction component list to `reactions` reactions with a fractional
 * `overage` (e.g. 0.1 for 10% extra). Returns null on invalid input.
 */
export function masterMix(
  components: PcrComponent[],
  reactions: number,
  overage: number,
): PcrResult | null {
  if (!Number.isFinite(reactions) || reactions <= 0) return null;
  if (!Number.isFinite(overage) || overage < 0) return null;
  const factor = reactions * (1 + overage);
  const rows: PcrRow[] = components.map((c) => ({
    ...c,
    total: Math.max(0, c.perRxn) * factor,
  }));
  const perTube = components.reduce((a, c) => a + Math.max(0, c.perRxn), 0);
  return {
    rows,
    effectiveRxns: factor,
    perTube,
    totalVolume: perTube * factor,
  };
}
